/* eslint-disable @typescript-eslint/member-delimiter-style */
/* eslint-disable react/jsx-curly-newline */
import classnames from 'classnames'
import { Component, ComponentChildren, FunctionalComponent } from 'preact'
import TextButton from '../../control-components/ui-components/TextButton'
import { getFirstChild, isFunction } from '../../libs/typeGuards'
import GlobalClick from '../interactive/documentClick'

interface TriggerProps {
  onClick?: (e: Event) => void
  disabled?: boolean
}

interface Props {
  popupName: string
  triggerClass?: string
  class?: string
  triggerElement?: FunctionalComponent<TriggerProps> | string
  trigger?: ComponentChildren | ((isTriggerOpen: boolean) => ComponentChildren)
  children: ComponentChildren
  closeOnDropdownClick?: boolean
  openOnHover?: boolean
  disabled?: boolean
  onGlobalKey?: (dir: 'up' | 'down' | 'enter') => void
}

interface State {
  isOpen: boolean
  isTriggerClicked: boolean
}

export default class extends Component<Props, State> {
  hoverEnterTimeout: NodeJS.Timeout | null = null
  hoverLeaveTimeout: NodeJS.Timeout | null = null

  constructor(props: Props) {
    super(props)
    this.handleEscapeClick = this.handleEscapeClick.bind(this)
  }

  handleEscapeClick(e: KeyboardEvent) {
    if (e.code === 'Escape') {
      this.setState({ isOpen: false })
      return
    }
    const arrowKeyCallback = this.props.onGlobalKey
    if (!arrowKeyCallback || !this.state.isOpen) return

    if (e.code === 'Enter') {
      arrowKeyCallback('enter')
    } else if (e.code === 'ArrowDown') {
      arrowKeyCallback('down')
      e.preventDefault()
    } else if (e.code === 'ArrowUp') {
      arrowKeyCallback('up')
      e.preventDefault()
    }
  }

  componentDidMount() {
    window.addEventListener('keydown', this.handleEscapeClick)
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleEscapeClick)
  }

  render(props: Props) {
    let trigger: ComponentChildren = null
    if (typeof props.trigger === 'function') {
      trigger = props.trigger(this.state.isOpen)
    }
    const Trigger = props.triggerElement || TextButton
    const renderPropChildren = getFirstChild(props.children)
    return (
      <div
        className={classnames('dropdown-container', props.triggerClass)}
        onMouseEnter={
          props.openOnHover
            ? () => {
                if (this.hoverLeaveTimeout) clearTimeout(this.hoverLeaveTimeout)
                this.hoverEnterTimeout = setTimeout(
                  () => this.setState({ isOpen: true, isTriggerClicked: true }),
                  100
                )
              }
            : undefined
        }
        onMouseLeave={
          props.openOnHover
            ? () => {
                if (this.hoverEnterTimeout) clearTimeout(this.hoverEnterTimeout)
                this.hoverLeaveTimeout = setTimeout(
                  () => this.setState({ isOpen: false, isTriggerClicked: false }),
                  100
                )
              }
            : undefined
        }
      >
        <Trigger
          aria-haspopup={props.popupName}
          aria-expanded={this.state.isOpen}
          disabled={props.disabled}
          onClick={() => {
            if (props.disabled || props.openOnHover) {
              return
            }
            this.setState({ isOpen: !this.state.isOpen, isTriggerClicked: true })
          }}
        >
          {trigger ?? props.trigger}
        </Trigger>
        <GlobalClick
          Component='ul'
          tabIndex={-1}
          role={props.popupName}
          className={classnames(props.class, {
            'is-hidden': !this.state.isOpen
          })}
          onChildrenClick={() =>
            props.closeOnDropdownClick
              ? this.setState({ isOpen: false, isTriggerClicked: false })
              : ''
          }
          onGlobalClick={() => {
            this.state.isTriggerClicked
              ? this.setState({ isTriggerClicked: false })
              : this.setState({ isOpen: false, isTriggerClicked: false })
          }}
        >
          {isFunction(renderPropChildren) &&
            renderPropChildren({
              close: () => this.setState({ isOpen: false, isTriggerClicked: false })
            })}
          {props.children}
        </GlobalClick>
      </div>
    )
  }
}
